import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  User, Award, BadgeCheck, BookOpen, FolderOpen,
  FileQuestion, BrainCircuit, ChevronDown, LogOut,
  Lock, Zap, ChevronRight, Sparkles, IdCard, CreditCard,
  MessageSquare,
} from "lucide-react";
import TierBadge from "./access/TierBadge";
import useAccess from "../hooks/useAccess";

const TIER_ORDER = ["free", "wanderer", "kindred", "quester", "pilgrim", "guardian", "spire"];
const getTierRank = (key) => {
  const r = TIER_ORDER.indexOf((key || "free").toLowerCase());
  return r === -1 ? 0 : r;
};

const TIER_LABELS = {
  free: "Free", wanderer: "Wanderer", kindred: "Kindred Spirit",
  quester: "Quester", pilgrim: "Pilgrim", guardian: "Guardian", spire: "Spire",
};

const UserDropdown = ({ userData, logOut }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { tierKey, meetsMinTier, isReviewer, isMentor } = useAccess();

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const menuItems = [
    {
      label: "Profile",
      icon: User,
      path: `/profile/${userData?.id || userData?._id}`,
      locked: false,
      minTier: null,
    },
    {
      label: "My Membership",
      icon: CreditCard,
      path: "/membership",
      locked: false,
      minTier: null,
    },
    {
      label: "Education",
      icon: BookOpen,
      path: "/education",
      locked: !meetsMinTier("wanderer"),
      minTier: "wanderer",
    },
    {
      label: "Awards",
      icon: Award,
      path: "/awards",
      locked: !meetsMinTier("wanderer"),
      minTier: "wanderer",
    },
    {
      label: "Badges",
      icon: BadgeCheck,
      path: "/badges",
      locked: !meetsMinTier("wanderer"),
      minTier: "wanderer",
    },
    {
      label: "Quests",
      icon: FileQuestion,
      path: "/quests",
      locked: false,
      minTier: null,
    },
    ...(isReviewer
      ? [{
          label: "Reviewer",
          icon: BadgeCheck,
          path: "/reviewer",
          locked: false,
          minTier: null,
        }]
      : []),
    ...(isMentor
      ? [{
          label: "Mentor",
          icon: MessageSquare,
          path: "/mentor",
          locked: false,
          minTier: null,
        }]
      : []),
    ...((userData && !isReviewer && !isMentor)
      ? [{
          label: "My Messages",
          icon: MessageSquare,
          path: "/mentor-messages",
          locked: false,
          minTier: null,
        }]
      : []),
    {
      label: "Quiz",
      icon: BrainCircuit,
      path: "/quiz",
      locked: false,          // free: can view list (attempt locked in-page)
      minTier: null,
    },
    {
      label: "Projects",
      icon: FolderOpen,
      path: "/projects",
      locked: !meetsMinTier("kindred"),
      minTier: "kindred",
    },
  ];

  const handleItemClick = (item) => {
    setIsOpen(false);
    navigate(item.locked ? "/membership" : item.path);
  };

  const isFree = getTierRank(tierKey) < getTierRank("wanderer");
  const fullName = [userData?.firstName, userData?.lastName].filter(Boolean).join(" ").trim();
  const displayName = userData?.preferredName || fullName || userData?.memberId || "Member";
  const avatarInitial = displayName.charAt(0).toUpperCase();

  return (
    <div className="relative" ref={dropdownRef}>

      {/* ── Trigger ─────────────────────────────────────────── */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="hidden md:flex items-center gap-2.5 group focus:outline-none"
      >
        {/* Avatar */}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#B6FCFF] to-[#00C3FF] flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200 overflow-hidden">
          {userData?.pfp ? (
            <img src={userData.pfp} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-black font-bold font-cinzel text-sm select-none">
              {avatarInitial}
            </span>
          )}
        </div>

        {/* Name + tier */}
        <div className="flex flex-col items-start leading-tight">
          <span className="text-white font-outfit font-medium text-sm">
            {displayName}
          </span>
          <TierBadge tierKey={tierKey} size="sm" />
        </div>

        <ChevronDown
          size={15}
          className={`text-white/70 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {/* ── Dropdown ─────────────────────────────────────────── */}
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          <div className="absolute right-0 mt-3 w-72 z-50">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">

              {/* Header */}
              <div className="px-4 py-4 bg-gradient-to-r from-[#B6FCFF]/30 to-[#00C3FF]/20 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#B6FCFF] to-[#00C3FF] flex items-center justify-center flex-shrink-0 shadow overflow-hidden">
                    {userData?.pfp ? (
                      <img src={userData.pfp} alt={displayName} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-black font-bold font-cinzel select-none">
                        {avatarInitial}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-semibold text-gray-900 font-cinzel text-sm truncate">
                      {displayName}
                    </p>
                    <p className="text-xs text-gray-500 font-outfit truncate">{userData?.email}</p>
                    <div className="mt-1.5">
                      <TierBadge tierKey={tierKey} size="sm" />
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex gap-3 mt-3 pt-3 border-t border-white/60">
                  <div className="flex items-center gap-1.5 flex-1">
                    <div className="w-7 h-7 rounded-lg bg-white/70 flex items-center justify-center">
                      <IdCard size={14} className="text-gray-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-outfit leading-none">Member ID</p>
                      <p className="text-xs font-semibold text-gray-700 font-outfit">
                        {userData?.memberId || "—"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-1">
                    <div className="w-7 h-7 rounded-lg bg-white/70 flex items-center justify-center">
                      <Zap size={14} className="text-amber-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-outfit leading-none">Sparks</p>
                      <p className="text-xs font-semibold text-amber-600 font-outfit">
                        {userData?.growthSparks || 0}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-1">
                    <div className="w-7 h-7 rounded-lg bg-white/70 flex items-center justify-center">
                      <BadgeCheck size={14} className="text-cyan-500" />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 font-outfit leading-none">Badges</p>
                      <p className="text-xs font-semibold text-gray-700 font-outfit">
                        {userData?.badges?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1.5">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.label}
                      onClick={() => handleItemClick(item)}
                      className={`w-full px-4 py-2.5 flex items-center justify-between group transition-colors duration-150
                        ${item.locked
                          ? "hover:bg-amber-50 cursor-pointer"
                          : "hover:bg-gray-50 cursor-pointer"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors
                          ${item.locked
                            ? "bg-amber-50 group-hover:bg-amber-100"
                            : "bg-gray-100 group-hover:bg-cyan-50"
                          }`}>
                          <Icon
                            size={14}
                            className={item.locked
                              ? "text-amber-400"
                              : "text-gray-500 group-hover:text-cyan-600"
                            }
                          />
                        </div>
                        <span className={`font-outfit font-medium text-sm transition-colors
                          ${item.locked ? "text-gray-400" : "text-gray-700 group-hover:text-gray-900"}`}>
                          {item.label}
                        </span>
                      </div>

                      {item.locked ? (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] font-semibold text-amber-500 font-outfit capitalize">
                            {TIER_LABELS[item.minTier]}+
                          </span>
                          <Lock size={11} className="text-amber-400" />
                        </div>
                      ) : (
                        <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-400 transition-colors" />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Upgrade Banner — free tier only */}
              {isFree && (
                <div className="mx-3 mb-3 p-3 rounded-xl bg-gradient-to-r from-[#B6FCFF]/40 to-[#00C3FF]/30 border border-cyan-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-cyan-600 flex-shrink-0" />
                    <p className="font-outfit text-xs text-gray-700 font-medium">
                      Unlock quests, quiz, projects & more
                    </p>
                  </div>
                  <button
                    onClick={() => { navigate("/membership"); setIsOpen(false); }}
                    className="w-full py-1.5 rounded-lg bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] text-black text-xs font-semibold font-outfit hover:opacity-90 transition-opacity"
                  >
                    Upgrade Membership
                  </button>
                </div>
              )}

              {/* Logout */}
              <div className="border-t border-gray-100 py-1.5">
                <button
                  onClick={() => { logOut(); setIsOpen(false); }}
                  className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-red-50 group transition-colors duration-150"
                >
                  <div className="w-7 h-7 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                    <LogOut size={14} className="text-red-400 group-hover:text-red-500 transition-colors" />
                  </div>
                  <span className="font-outfit font-medium text-sm text-red-500 group-hover:text-red-600">
                    Logout
                  </span>
                </button>
              </div>

            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDropdown;
